<template>
  <span class="llm-history-versions">
    <a
      href="javascript:void(0)"
      class="llm-history-versions__trigger"
      @click="open"
    >
      <Icon type="ios-time-outline" :size="16" class="llm-history-versions__trigger-icon" />
      历史版本
    </a>
    <Drawer
      v-model="visible"
      title="历史版本"
      placement="right"
      :width="drawerWidth"
      :mask-closable="true"
      :closable="true"
      footer-hide
      class-name="llm-history-versions-drawer"
      @on-visible-change="onVisibleChange"
    >
      <div class="llm-history-versions-body">
        <Spin v-if="loading" fix />
        <div v-show="!loading" class="llm-history-list">
          <div
            v-for="(row, index) in sortedList"
            :key="row.id"
            class="llm-history-card"
            :class="{ 'llm-history-card--latest': index === 0 }"
          >
            <div class="llm-history-card__main">
              <div class="llm-history-card__info">
                <div class="llm-history-card__title-row">
                  <span class="llm-history-card__title">版本 {{ versionLabel(index) }}</span>
                  <span v-if="index === 0" class="llm-history-card__badge">最新</span>
                </div>
                <div class="llm-history-card__time">
                  发布时间：{{ formatCreateAt(row.createAt) }}
                </div>
              </div>
              <div class="llm-history-card__actions">
                <a
                  href="javascript:void(0)"
                  class="llm-history-card__preview"
                  @click.prevent="openPreview(row)"
                >预览</a>
                <Button
                  class="llm-history-card__restore"
                  :loading="restoringRowId != null && restoringRowId === row.id"
                  :disabled="restoringRowId != null"
                  @click="confirmRestoreVersion(row)"
                >恢复</Button>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="!loading && sceneId && (!sortedList || !sortedList.length)"
          class="llm-history-versions__empty"
          role="status"
        >
          <div class="llm-history-versions__empty-icon" aria-hidden="true">
            <Icon type="ios-time-outline" :size="48" />
          </div>
          <p class="llm-history-versions__empty-text">暂无数据</p>
        </div>
      </div>
    </Drawer>

    <Modal
      v-model="previewVisible"
      title=""
      :closable="false"
      :footer-hide="true"
      width="800"
      class-name="llm-history-versions-preview-modal"
      @on-cancel="closePreview"
    >
      <div class="llm-version-preview">
        <div class="llm-version-preview__header">
          <div class="llm-version-preview__header-text">
            <div class="llm-version-preview__header-title">版本预览</div>
            <div class="llm-version-preview__header-time">
              发布时间：{{ previewPublishTime || '—' }}
            </div>
          </div>
          <a
            href="javascript:void(0)"
            class="llm-version-preview__close"
            aria-label="关闭"
            @click="closePreview"
          >
            <Icon type="ios-close" size="28" />
          </a>
        </div>

        <div class="llm-version-preview__body">
          <div
            v-if="previewSections.length"
            class="llm-version-preview__scroll"
          >
            <div
              v-for="(sec, idx) in previewSections"
              :key="idx"
              class="llm-version-preview__card"
            >
              <div class="llm-version-preview__card-title">{{ sec.title }}</div>
              <div
                class="llm-version-preview__card-body"
                :class="{ 'is-pre': sec.pre }"
              >{{ sec.body }}</div>
            </div>
          </div>
          <p v-else class="llm-history-versions-preview-empty-tip">
            暂无预览内容
          </p>
        </div>

        <div class="llm-version-preview__footer">
          <Button class="llm-version-preview__btn-close" @click="closePreview">关闭</Button>
          <Button
            type="primary"
            class="llm-version-preview__btn-restore"
            :loading="
              !!previewRestoreRow &&
              restoringRowId != null &&
              restoringRowId === previewRestoreRow.id
            "
            :disabled="!previewRestoreRow || restoringRowId != null"
            @click="confirmRestoreFromPreview"
          >恢复此版本</Button>
        </div>
      </div>
    </Modal>

    <Modal
      v-model="restoreConfirmVisible"
      :title="restoreConfirmModalTitle"
      width="416"
      :z-index="4000"
      :mask-closable="false"
      :closable="!restoreConfirmOkLoading"
      class-name="llm-history-restore-confirm-modal"
      @on-cancel="closeRestoreConfirm"
    >
      <p class="llm-history-restore-confirm-body">{{ restoreConfirmContent() }}</p>
      <div slot="footer" class="llm-history-restore-confirm-footer">
        <Button class="llm-history-restore-confirm-btn-cancel" @click="closeRestoreConfirm">
          取消
        </Button>
        <Button
          type="primary"
          class="llm-history-restore-confirm-btn-ok"
          :loading="restoreConfirmOkLoading"
          @click="submitRestoreConfirm"
        >
          确定
        </Button>
      </div>
    </Modal>
  </span>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';
import {
  getSceneReleaseJsonBySceneIdA,
  sceneReleaseRollbackA,
} from '@/server/api';
import { parseLlmsettingsV2, validateLlmsettingsSchema } from '../../utils/llmsettingsSchema';
import { coerceLlmsettingsForUi } from '../../utils/llmsettingsMigrate';
import { formatIntentTagSummary } from '../../utils/intentTagHelpers';

export default {
  name: 'LlmHistoryVersions',
  props: {
    sceneId: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      visible: false,
      loading: false,
      restoringRowId: null,
      list: [],
      previewVisible: false,
      previewLlm: null,
      previewPublishTime: '',
      previewRestoreRow: null,
      restoreConfirmVisible: false,
      restoreConfirmRow: null,
      restoreConfirmFromPreview: false,
      restoreConfirmOkLoading: false,
    };
  },
  computed: {
    ...mapState('user', {
      companyState: (state) => state.company,
    }),
    companyId() {
      const c = this.companyState || {};
      return c.id != null ? Number(c.id) : c.company_id != null ? Number(c.company_id) : 0;
    },
    sortedList() {
      const rows = Array.isArray(this.list) ? [...this.list] : [];
      return rows.sort((a, b) => {
        const ta = Number(a && a.createAt) || 0;
        const tb = Number(b && b.createAt) || 0;
        return tb - ta;
      });
    },
    drawerWidth() {
      if (typeof window === 'undefined') return 400;
      const w = Math.round(window.innerWidth * 0.3);
      return Math.min(Math.max(w, 320), 560);
    },
    /** 按 llmsettings v2 区块展示预览 */
    previewSections() {
      return this.buildPreviewSectionsFromLlmsettings(this.previewLlm);
    },
    restoreConfirmModalTitle() {
      return this.restoreConfirmFromPreview ? '恢复此版本' : '恢复版本';
    },
  },
  methods: {
    open() {
      if (!this.sceneId) {
        this.$Message.warning('话术未加载，无法查看历史版本');
        return;
      }
      this.visible = true;
    },
    onVisibleChange(show) {
      if (show) this.fetchList();
    },
    /** 统一列表项字段（兼容不同字段名） */
    normalizeReleaseRow(raw) {
      if (!raw || typeof raw !== 'object') return null;
      const id = raw.id ?? raw.releaseId ?? raw.ReleaseId;
      if (id == null) return null;
      const createAt = raw.createAt ?? raw.create_at ?? raw.CreateAt ?? raw.createdAt;
      const row = { id: Number(id), createAt };
      // 列表接口 get/scene/json/release 返回的快照字段名兼容（与后端 camel/snake 一致）
      const sceneJson =
        raw.sceneJson ??
        raw.SceneJson ??
        raw.scene_json ??
        raw.Scene_JSON;
      if (sceneJson !== undefined) row.sceneJson = sceneJson;
      const scene =
        raw.scene ?? raw.Scene ?? raw.scene_string ?? raw.SceneString;
      if (scene !== undefined) row.scene = scene;
      return row;
    },
    fetchList() {
      if (!this.sceneId) return;
      if (!this.companyId) {
        this.$Message.warning('公司信息未加载，无法拉取历史版本');
        this.list = [];
        return;
      }
      this.loading = true;
      getSceneReleaseJsonBySceneIdA({
        company_id: this.companyId,
        scene_id: this.sceneId,
      })
        .then((res) => {
          if (!res || res.code !== 200) return;
          let rows = [];
          const d = res.data;
          if (Array.isArray(d)) rows = d;
          else if (d && Array.isArray(d.list)) rows = d.list;
          else if (d && Array.isArray(d.records)) rows = d.records;
          else if (d && Array.isArray(d.data)) rows = d.data;
          this.list = rows.map((r) => this.normalizeReleaseRow(r)).filter(Boolean);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatCreateAt(ts) {
      if (ts == null) return '—';
      return moment(Number(ts) * 1000).format('YYYY-MM-DD HH:mm:ss');
    },
    versionLabel(index) {
      const n = this.sortedList.length;
      return n - index;
    },
    parseSceneFromReleaseDetail(raw) {
      const sceneJsonStr =
        (raw &&
          (raw.sceneJson != null
            ? raw.sceneJson
            : raw.SceneJson != null
              ? raw.SceneJson
              : raw.scene_json != null
                ? raw.scene_json
                : raw.scene)) ||
        '{}';
      try {
        return typeof sceneJsonStr === 'string'
          ? JSON.parse(sceneJsonStr)
          : sceneJsonStr;
      } catch (e) {
        return null;
      }
    },
    /** 列表项是否带有可解析的快照（兼容多种字段名） */
    rowHasReleaseSnapshot(row) {
      if (!row || typeof row !== 'object') return false;
      const sj =
        row.sceneJson ??
        row.SceneJson ??
        row.scene_json;
      if (sj != null && sj !== '') {
        if (typeof sj === 'string') return String(sj).trim() !== '';
        return true;
      }
      const sc = row.scene ?? row.Scene;
      if (sc != null && sc !== '') {
        if (typeof sc === 'string') return String(sc).trim() !== '';
        return true;
      }
      return false;
    },
    buildPreviewSectionsFromLlmsettings(raw) {
      const check = validateLlmsettingsSchema(raw);
      if (!check.valid || check.empty) return [];

      const { prompt_config: pc, intent_and_tags: it } = parseLlmsettingsV2(raw);
      const sections = [];
      const rs = (pc && pc.role_setting) || {};

      const roleLines = [];
      if (rs.nickname != null && String(rs.nickname).trim()) {
        roleLines.push(`姓名：${String(rs.nickname).trim()}`);
      }
      const identityTexts = (Array.isArray(rs.identity) ? rs.identity : [])
        .filter((i) => i && i.selection && String(i.role_name || '').trim())
        .map((i) => String(i.role_name).trim());
      if (identityTexts.length) roleLines.push(`身份：${identityTexts.join('、')}`);
      const styleTexts = (Array.isArray(rs.communication_style) ? rs.communication_style : [])
        .filter((i) => i && i.selection && String(i.communication_style_name || '').trim())
        .map((i) => String(i.communication_style_name).trim());
      if (styleTexts.length) roleLines.push(`沟通风格：${styleTexts.join('、')}`);
      if (roleLines.length) {
        sections.push({ title: '角色信息', body: roleLines.join('\n'), pre: false });
      }
      if (rs.description != null && String(rs.description).trim()) {
        sections.push({ title: '角色描述', body: String(rs.description).trim(), pre: false });
      }
      if (pc.custom_prompt != null && String(pc.custom_prompt).trim()) {
        sections.push({
          title: '自由模式',
          body: String(pc.custom_prompt).trim(),
          pre: false,
        });
      }

      (Array.isArray(pc.business_flow) ? pc.business_flow : []).forEach((block) => {
        const body = block && block.content != null ? String(block.content).trim() : '';
        if (!body) return;
        const title =
          block && block.title != null && String(block.title).trim()
            ? String(block.title).trim()
            : '版块';
        sections.push({ title, body, pre: false });
      });

      const rules = Array.isArray(it.intent_tags) ? it.intent_tags : [];
      const intentText = rules
        .filter((r) => {
          if (!r) return false;
          const name = r.tag_name && String(r.tag_name).trim();
          const desc = r.tag_description && String(r.tag_description).trim();
          const hasConditions = Array.isArray(r.conditions) && r.conditions.length > 0;
          return Boolean(name || desc || hasConditions);
        })
        .map((r) => {
          const name = String((r && r.tag_name) || '').trim();
          const summary = formatIntentTagSummary({
            judge: r && r.judge === 'rule' ? 'rule' : 'prompt',
            rule: r && r.tag_description != null ? String(r.tag_description) : '',
            conditions: Array.isArray(r.conditions)
              ? r.conditions.map((cond) => ({
                  fieldId:
                    cond && cond.field_id != null
                      ? String(cond.field_id)
                      : cond && cond.fieldId != null
                        ? String(cond.fieldId)
                        : '',
                  operator: cond && cond.operator != null ? String(cond.operator) : 'eq',
                  value: cond && cond.value != null ? String(cond.value) : '',
                }))
              : [],
          });
          const ranking =
            r && r.ranking != null && Number(r.ranking) > 0 ? Number(r.ranking) : ''
          return `${ranking ? `${ranking}. ` : ''}${name}：${summary}`;
        })
        .join('\n');
      if (intentText.trim()) {
        sections.push({ title: '意向标签', body: intentText, pre: false });
      }

      const extractForm =
        it.extract_form && typeof it.extract_form === 'object' ? it.extract_form : null;
      const extractFields = extractForm
        ? Array.isArray(extractForm.form_fields)
          ? extractForm.form_fields
          : Array.isArray(extractForm.fields)
            ? extractForm.fields
            : []
        : Array.isArray(it.extract_fields)
          ? it.extract_fields
          : [];
      const extractFormName = extractForm
        ? String(extractForm.form_name || '').trim()
        : String((it && it.extract_form_name) || '').trim();
      if (extractFormName || extractFields.length) {
        const fieldLines = [];
        if (extractFormName) {
          fieldLines.push(`信息采集名称：${extractFormName}`);
        }
        const fieldText = extractFields
          .map((field) => {
            const name = String((field && field.field_name) || '').trim();
            const type = String((field && field.field_type) || '').trim();
            const instruction = String((field && field.extract_instruction) || '').trim();
            const allowed = Array.isArray(field.allowed_values)
              ? field.allowed_values.map((item) => String(item)).filter(Boolean).join('、')
              : '';
            return [name, type, allowed ? `允许值：${allowed}` : '', instruction ? `说明：${instruction}` : '']
              .filter(Boolean)
              .join('\n');
          })
          .filter(Boolean);
        fieldLines.push(...fieldText);
        const body = fieldLines.join('\n\n');
        if (body.trim()) {
          sections.push({ title: '信息采集', body, pre: false });
        }
      }

      const ratingRules = Array.isArray(it.rating_rules) ? it.rating_rules : [];
      if (ratingRules.length) {
        const ruleText = ratingRules
          .map((rule, index) => {
            const name = String((rule && rule.rule_name) || '').trim();
            const conditions = Array.isArray(rule.conditions) ? rule.conditions : [];
            const logic = conditions
              .map((cond) => {
                const fieldId = String((cond && cond.field_id) || '');
                const field = extractFields.find((item) => String(item.field_id) === fieldId);
                const fieldName = field ? String(field.field_name || '').trim() : '未知字段';
                if (cond.operator === 'collected') return `${fieldName} 已采集`;
                return `${fieldName} 等于 ${String((cond && cond.value) || '').trim()}`;
              })
              .join(' 且 ');
            const ranking =
              rule && rule.ranking != null && Number(rule.ranking) > 0
                ? Number(rule.ranking)
                : index + 1;
            return `${ranking}. ${name}${logic ? `\n${logic}` : ''}`;
          })
          .filter(Boolean)
          .join('\n\n');
        if (ruleText.trim()) {
          sections.push({ title: '评级规则', body: ruleText, pre: false });
        }
      }

      return sections;
    },
    openPreview(row) {
      if (!row || row.id == null) return;
      this.previewRestoreRow = row;
      this.previewPublishTime = this.formatCreateAt(row.createAt);
      this.previewVisible = true;

      const hasPayload = this.rowHasReleaseSnapshot(row);
      if (!hasPayload) {
        this.previewLlm = {};
        this.$Message.warning('该版本缺少快照数据，请关闭抽屉后重新打开「历史版本」以刷新列表');
        return;
      }

      const scene = this.parseSceneFromReleaseDetail(row);
      if (!scene) {
        this.previewLlm = {};
        this.$Message.error('版本快照解析失败');
        return;
      }
      const llmsettings =
        scene.llmsettings && typeof scene.llmsettings === 'object' ? scene.llmsettings : {};
      this.previewLlm = coerceLlmsettingsForUi(llmsettings);
    },
    closePreview() {
      this.previewVisible = false;
      this.previewLlm = null;
      this.previewRestoreRow = null;
      this.previewPublishTime = '';
    },
    restoreConfirmContent() {
      return '确定恢复该版本吗？将用该版本替换当前全部大模型配置（提示词配置、模型与语音、通话控制、意向与标签、自由模式、工具等）。请及时保存。';
    },
    /** 列表「恢复」二次确认 */
    confirmRestoreVersion(row) {
      if (!row || row.id == null) return;
      this.restoreConfirmRow = row;
      this.restoreConfirmFromPreview = false;
      this.restoreConfirmVisible = true;
    },
    /** 预览弹窗「恢复此版本」二次确认 */
    confirmRestoreFromPreview() {
      const row = this.previewRestoreRow;
      if (!row || row.id == null) return;
      this.restoreConfirmRow = row;
      this.restoreConfirmFromPreview = true;
      this.restoreConfirmVisible = true;
    },
    closeRestoreConfirm() {
      if (this.restoreConfirmOkLoading) return;
      this.restoreConfirmVisible = false;
      this.restoreConfirmRow = null;
    },
    submitRestoreConfirm() {
      const row = this.restoreConfirmRow;
      if (!row || row.id == null) return;
      this.restoreConfirmOkLoading = true;
      const closePreviewAfter = this.restoreConfirmFromPreview;
      this.restoreVersion(row, { closePreviewAfter }).finally(() => {
        this.restoreConfirmOkLoading = false;
        this.restoreConfirmVisible = false;
        this.restoreConfirmRow = null;
      });
    },
    restoreVersion(row, options = {}) {
      const { closePreviewAfter } = options;
      if (!row || row.id == null) return Promise.resolve();
      if (!this.companyId) {
        this.$Message.warning('公司信息未加载，无法恢复版本');
        return Promise.resolve();
      }

      if (!this.rowHasReleaseSnapshot(row)) {
        this.$Message.error('缺少版本快照数据，请关闭抽屉后重新打开「历史版本」再试');
        return Promise.reject(new Error('no snapshot'));
      }
      const scenePreview = this.parseSceneFromReleaseDetail(row);
      if (!scenePreview) {
        this.$Message.error('历史话术数据解析失败');
        return Promise.reject(new Error('parse'));
      }

      this.restoringRowId = row.id;
      return sceneReleaseRollbackA({
        companyId: this.companyId,
        ReleaseId: row.id,
        /** 是否同步进行中的任务；默认不回写任务，仅话术版本对齐 */
        ApplyAll: 'false',
      })
        .then((rollbackRes) => {
          if (!rollbackRes || rollbackRes.code !== 200) {
            const msg =
              (rollbackRes && rollbackRes.message) || rollbackRes.msg || '版本回滚失败';
            this.$Message.error(msg);
            return Promise.reject(new Error(msg));
          }
          // 与预览一致：使用列表项中的 sceneJson，不再请求 release/one
          const scene = scenePreview;
          const llmsettings =
            scene && scene.llmsettings && typeof scene.llmsettings === 'object'
              ? scene.llmsettings
              : {};
          this.$emit('restore', { llmsettings });
          this.visible = false;
          if (closePreviewAfter) this.closePreview();
          this.$Message.success('已恢复该版本的全部配置，请保存以写入话术');
        })
        .catch(() => {})
        .finally(() => {
          this.restoringRowId = null;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.llm-history-versions {
  display: inline-flex;
  align-items: center;
}
.llm-history-versions__trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: #8c8c8c;
  &:hover {
    color: #2d8cf0;
  }
}
.llm-history-versions__trigger-icon {
  flex-shrink: 0;
}

.llm-history-versions-body {
  position: relative;
  min-height: 160px;
}

.llm-history-list {
  max-height: calc(100vh - 140px);
  overflow-y: auto;
  padding: 4px 2px 8px;
}

.llm-history-card {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px 18px;
  margin-bottom: 12px;
  background: #fff;
  box-sizing: border-box;

  &:last-child {
    margin-bottom: 0;
  }

  &--latest {
    border-color: #2d8cf0;
    background: #f7fbff;
  }
}

.llm-history-card__main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 52px;
}

.llm-history-card__info {
  flex: 1;
  min-width: 0;
}

.llm-history-card__title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 6px;
}

.llm-history-card__title {
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  line-height: 1.4;
}

.llm-history-card__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  height: 22px;
  font-size: 12px;
  color: #fff;
  background: #2d8cf0;
  border-radius: 4px;
  line-height: 22px;
}

.llm-history-card__time {
  font-size: 13px;
  color: #8c8c8c;
  line-height: 1.5;
}

.llm-history-card__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 16px;
}

.llm-history-card__preview {
  font-size: 14px;
  color: #8c8c8c;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    color: #2d8cf0;
  }
}

.llm-history-card__restore.ivu-btn {
  min-width: 72px;
  height: 32px;
  padding: 0 16px;
  font-size: 14px;
  color: #262626;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  &:hover:not(:disabled) {
    color: #2d8cf0;
    border-color: #2d8cf0;
  }
}

.llm-history-versions__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 48px 16px 40px;
  min-height: 220px;
  text-align: center;
  box-sizing: border-box;
}

.llm-history-versions__empty-icon {
  line-height: 1;
  margin-bottom: 12px;
  color: #d9d9d9;
}

.llm-history-versions__empty-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #8c8c8c;
}
</style>

<style lang="scss">
.llm-history-versions-drawer {
  .ivu-drawer-body {
    position: relative;
    min-height: 160px;
    padding-bottom: 20px;
  }
}

.llm-history-versions-preview-modal {
  .ivu-modal-header {
    display: none;
  }
  .ivu-modal-body {
    padding: 0;
  }
  .llm-version-preview {
    display: flex;
    flex-direction: column;
    max-height: min(820px, calc(100vh - 120px));
  }
  .llm-version-preview__header {
    flex-shrink: 0;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 24px 12px;
    border-bottom: 1px solid #f0f0f0;
  }
  .llm-version-preview__header-text {
    flex: 1;
    min-width: 0;
  }
  .llm-version-preview__close {
    flex-shrink: 0;
    color: #8c8c8c;
    line-height: 1;
    margin-top: -4px;
    &:hover {
      color: #262626;
    }
  }
  .llm-version-preview__header-title {
    font-size: 18px;
    font-weight: 600;
    color: #262626;
    line-height: 1.4;
    margin-bottom: 8px;
  }
  .llm-version-preview__header-time {
    font-size: 13px;
    color: #8c8c8c;
    line-height: 1.5;
  }
  .llm-version-preview__body {
    position: relative;
    flex: 1;
    min-height: 300px;
    overflow: hidden;
  }
  .llm-version-preview__scroll {
    max-height: min(620px, calc(100vh - 180px));
    overflow-y: auto;
    padding: 16px 24px 8px;
  }
  .llm-version-preview__card {
    margin-bottom: 12px;
    padding: 14px 16px;
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    box-sizing: border-box;
  }
  .llm-version-preview__card-title {
    font-size: 14px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 10px;
    line-height: 1.4;
  }
  .llm-version-preview__card-body {
    font-size: 14px;
    color: #262626;
    line-height: 1.65;
    word-break: break-word;
    white-space: pre-wrap;
    &.is-pre {
      font-family: inherit;
    }
  }
  .llm-version-preview__footer {
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    padding: 12px 24px 16px;
    border-top: 1px solid #f0f0f0;
    background: #fff;
  }
  .llm-version-preview__btn-close.ivu-btn {
    min-width: 72px;
    height: 34px;
    color: #262626;
    background: #fff;
    border: 1px solid #d9d9d9;
  }
  .llm-version-preview__btn-restore.ivu-btn {
    min-width: 104px;
    height: 34px;
  }
  .llm-history-versions-preview-empty-tip {
    margin: 24px 0 8px;
    font-size: 13px;
    color: #8c8c8c;
    text-align: center;
    padding: 48px 16px 32px;
  }
}

/* 「恢复版本」二次确认：与设计稿一致（顶栏标题+分割线、正文左对齐、底栏右对齐主次按钮） */
.llm-history-restore-confirm-modal {
  .ivu-modal-content {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
  .ivu-modal-header {
    padding: 16px 24px;
    border-bottom: 1px solid #f0f0f0;
    .ivu-modal-header-inner {
      font-size: 16px;
      font-weight: 600;
      color: #262626;
      line-height: 1.5;
    }
  }
  .ivu-modal-close {
    top: 13px;
    right: 16px;
    z-index: 2;
    .ivu-icon-ios-close {
      font-size: 22px;
      color: #8c8c8c;
    }
    &:hover .ivu-icon-ios-close {
      color: #262626;
    }
  }
  .ivu-modal-body {
    padding: 20px 24px;
  }
  .ivu-modal-footer {
    padding: 10px 24px 16px;
    border-top: 1px solid #f0f0f0;
    background: #fff;
  }
}

.llm-history-restore-confirm-body {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #595959;
  text-align: left;
}

.llm-history-restore-confirm-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
}

.llm-history-restore-confirm-btn-cancel.ivu-btn {
  min-width: 64px;
  height: 32px;
  padding: 0 15px !important;
  font-size: 14px;
  line-height: 30px !important;
  color: #262626 !important;
  background: #fff !important;
  border: 1px solid #d9d9d9 !important;
  border-radius: 4px !important;
  &:hover {
    color: #1890ff !important;
    border-color: #1890ff !important;
    background: #fff !important;
  }
}

.llm-history-restore-confirm-btn-ok.ivu-btn {
  min-width: 64px;
  height: 32px;
  padding: 0 15px !important;
  font-size: 14px;
  line-height: 30px !important;
  border-radius: 4px !important;
}
</style>
